// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import builderFetch
import { builderFetch } from '@automator/builder/utils';

/**
 * Create an item of a specific type with a given code.
 *
 * @param {String} itemType - Type of the item to be created. Expected "trigger", "action", or "closure".
 * @param {String} itemCode - Unique code for the item.
 * @param {object} [options={}] - Optional parameters.
 * @param {Number} [options.parentID=UncannyAutomator._recipe.recipe_id] - ID of the parent item.
 * @param {Array} options.actionsOrder - Order of actions.
 * @param {object} options.defaultMeta - Default metadata.
 * @throws Will throw an error if the item type or item code is invalid.
 * @return {Promise} - Returns a promise that resolves to the API response.
 */
const createItem = (
	itemType,
	itemCode,
	{
		parentID = UncannyAutomator._recipe.recipe_id,
		actionsOrder,
		defaultMeta
	} = {}
) => {
	// Event name
	const eventName = 'uap/builder/core/item/create-item';

	// Check for valid item type
	if ( ! [ 'trigger', 'action', 'closure' ].includes( itemType ) ) {
		throw new Error( 'Invalid item type provided. Expected "trigger", "action" or "closure".' );
	}

	// Check for valid item code
	if ( typeof itemCode !== 'string' ) {
		throw new Error( 'Invalid item code provided. Expected a string.' );
	}

	// Define action and snackbar messages based on item type
	let action, loadingMessage, successMessage;

	if ( itemType === 'trigger' ) {
		action = 'add-new-trigger';
		loadingMessage = escapeHTML( __( 'Adding trigger', 'uncanny-automator' ) );
		successMessage = escapeHTML( __( 'Trigger added successfully', 'uncanny-automator' ) );
	} else if ( itemType === 'action' ) {
		action = 'add-new-action';
		loadingMessage = escapeHTML( __( 'Adding action', 'uncanny-automator' ) );
		successMessage = escapeHTML( __( 'Action added successfully', 'uncanny-automator' ) );
	} else {
		action = 'add-new-closure';
		loadingMessage = escapeHTML( __( 'Adding action', 'uncanny-automator' ) );
		successMessage = escapeHTML( __( 'Action added successfully', 'uncanny-automator' ) );
	}

	// Send request to create the item
	return builderFetch( {
		eventName: eventName,
		endpoint: 'add',
		method: 'POST',
		data: {
			recipePostID: UncannyAutomator._recipe.recipe_id,
			// eslint-disable-next-line camelcase
			item_code: itemCode,
			action: action,
			// eslint-disable-next-line camelcase
			parent_id: parentID,
			// eslint-disable-next-line camelcase
			actions_order: actionsOrder,
			// eslint-disable-next-line camelcase
			default_meta: defaultMeta,
		},
		snackbar: {
			loading: loadingMessage,
			success: successMessage,
		}
	} ).then( response => {
		// Check if the call was successful
		if ( response.success ) {
			// Send custom event to the document
			document.dispatchEvent( new CustomEvent( eventName, {
				detail: {
					// eslint-disable-next-line camelcase
					itemCode: itemCode,
					action: action,
					// eslint-disable-next-line camelcase
					parentID: parentID,
					// eslint-disable-next-line camelcase
					actionsOrder: actionsOrder,
					// eslint-disable-next-line camelcase
					defaultMeta: defaultMeta,
				}
			} ) );
		}

		return response;
	} );
}

export default createItem;
